import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import type { Card } from '@tv-casino/shared';
import styles from './CardView.module.css';
import { SuitIcon } from './SuitIcon.js';

/** One playing card, or its back when `card` is omitted. */
export function CardView({
  card,
  large = false,
  delay = 0,
}: {
  card?: Card;
  large?: boolean;
  /** Milliseconds to hold before the deal animation starts. */
  delay?: number;
}) {
  const size = large ? ` ${styles.large}` : '';
  // A slot that stays mounted while its card turns face-up (the dealer's hole
  // card at reveal) reads as a flip; a fresh mount reads as a deal. The flag
  // and the delay are frozen once set — dropping them on a later render would
  // swap the animation-name back and restart the animation from scratch.
  const [stuckDelay] = useState(delay);
  const [everFlipped, setEverFlipped] = useState(false);
  const prev = useRef(card);
  useEffect(() => {
    if (prev.current === undefined && card !== undefined) setEverFlipped(true);
    prev.current = card;
  }, [card]);
  const style: CSSProperties | undefined = stuckDelay > 0 ? { animationDelay: `${stuckDelay}ms` } : undefined;
  if (!card) {
    return (
      <span
        className={`${styles.card} ${styles.back}${size}`}
        style={style}
        aria-label="Face-down card"
      />
    );
  }
  return (
    <span
      className={`${styles.card}${everFlipped ? ` ${styles.flipIn}` : ''}${size}`}
      style={style}
      data-suit={card.suit}
    >
      <span className={styles.rank}>{card.rank}</span>
      <SuitIcon className={styles.suit} suit={card.suit} />
    </span>
  );
}

/** A row of cards; `undefined` slots render as card backs. `overlap` fans them casino-style. */
export function CardRow({
  cards,
  large = false,
  overlap = false,
}: {
  cards: readonly (Card | undefined)[];
  large?: boolean;
  overlap?: boolean;
}) {
  // The server paces the deal, so cards normally arrive one at a time and land
  // as they come. A hand that fills from empty in a single state — a reconnect,
  // or the host un-hiding the table — gets the stagger instead of a pile.
  const prevCount = useRef(0);
  useEffect(() => {
    prevCount.current = cards.length;
  }, [cards.length]);
  const stagger = prevCount.current === 0 && cards.length > 1;
  return (
    <div className={overlap ? `${styles.row} ${styles.overlap}` : styles.row}>
      {cards.map((card, i) => (
        <CardView key={i} card={card} large={large} delay={stagger ? i * 110 : 0} />
      ))}
    </div>
  );
}
